import moment from 'moment';
import { useState, MouseEvent } from 'react';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showDialog } from '../actions';
import {
  selectSite,
  selectAlerts,
  selectNotifications,
  selectFollowUpNotifications,
  selectStalledClaimNotifications,
  selectClaimEmailApprovalNotifications,
  selectClaimCreatedNotifications,
  selectOperatorGroup,
  selectRole,
} from '../../store/authReducer';
import { selectMultiSiteSelection } from '../../views/site-selection/reducer';
import { selectCoreSiteId } from '../../views/settings/reducer';
import { useIntl } from 'react-intl';
import { MdOutlineEdit } from 'react-icons/md';
import { Alert } from './Alert';
import ToggleButton from './ToggleButton';
import { sendReclamationEmail } from '../../views/reclamations/actions';
import * as toastMessages from '../toastMessages';
import { useNotifications } from './alerts/useNotifications';
import { useAlerts } from './alerts/useAlerts';
import {
  getClaimIdFromNotification,
  getNotificationSiteId,
  getNotificationType,
} from './alerts/notificationHelpers';
import {
  parseNotificationBody,
  sanitizeHtmlContent,
  stripHtmlTags,
  normalizeExternalLink,
} from '../../views/alerts/utils';
import {
  CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
  CLAIM_CREATED_NOTIFICATION_TYPE,
  STALLED_CLAIM_NOTIFICATION_TYPE,
} from '../../views/alerts/actions';

const Alerts = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<number | null>(null);
  const [filteringRead, setFilteringRead] = useState(false);

  const alerts = useSelector(selectAlerts);
  const primarySiteId = useSelector(selectSite);
  const coreSiteId = useSelector(selectCoreSiteId);
  const multiSiteSelection = useSelector(selectMultiSiteSelection);
  const whatsNewNotifications = useSelector(selectNotifications);
  const followUpNotifications = useSelector(selectFollowUpNotifications);
  const stalledClaimNotifications = useSelector(selectStalledClaimNotifications);
  const claimEmailApprovalNotifications = useSelector(selectClaimEmailApprovalNotifications);
  const claimCreatedNotifications = useSelector(selectClaimCreatedNotifications);
  const operatorGroup = useSelector(selectOperatorGroup);
  const userRole = useSelector(selectRole);

  const { filteredAlerts, unreadCount } = useAlerts({
    alerts,
    selectedType,
    filteringRead,
    primarySiteId,
    multiSiteSelection,
  });

  const {
    readTrackingCount,
    claimNotifications,
    claimUnreadCount,
    markNotificationAsRead,
    handleNotificationOpen,
  } = useNotifications({
    primarySiteId,
    whatsNewNotifications,
    followUpNotifications,
    stalledClaimNotifications,
    claimEmailApprovalNotifications,
    claimCreatedNotifications,
  });

  const alertTypes: any = {
    Detergents: 0,
    Tasks: 1,
    Inventory: 2,
    'Whats New': 3,
    Claims: 4,
  };

  const isAdmin = (userRole || '').toLowerCase() === 'admin';
  const canManageWhatsNew = isAdmin;
  const whatsNewTypeValue = alertTypes['Whats New'];
  const isWhatsNewView = selectedType === whatsNewTypeValue;
  const claimsTypeValue = alertTypes.Claims;
  const isClaimsView = selectedType === claimsTypeValue;

  const handleNotificationList = () => (
    <div className="alerts-list">
      {filteredAlerts.map((n, i) => (
        <Alert alert={n} key={i} />
      ))}
    </div>
  );

  const alertTypeButton = (typeLabel: string, typeValue: number | null) => (
    <button
      onClick={() => setSelectedType(typeValue)}
      className={`${selectedType == typeValue ? 'active' : ''}`}
    >
      {typeLabel}
    </button>
  );

  const toggleOpen = () => setOpen(!open);
  const handleFilterToggle = (value: boolean) => setFilteringRead(value);

  const approveAndSendClaimEmail = (notification: any, event?: MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.stopPropagation();
    }

    const claimId = Number(getClaimIdFromNotification(notification));
    const siteId = Number(getNotificationSiteId(notification, primarySiteId));
    const operatorGroupDetails = operatorGroup?.details || {};
    const operatorGroupPayload = operatorGroup
      ? {
          name: operatorGroup.name,
          details: {
            email: operatorGroupDetails.email || operatorGroup.email,
            emailSendingDomain:
              operatorGroupDetails.emailSendingDomain || operatorGroup.emailSendingDomain,
          },
        }
      : null;

    if (!claimId || !siteId || !operatorGroupPayload) {
      toastMessages.generateToastr(
        'error',
        'Unable to send email',
        'Missing claim or operator group details.'
      );
      return;
    }

    dispatch(
      sendReclamationEmail({
        siteId,
        email: {
          id: claimId,
          templateKey: 'EmailCustomerClaimMessage',
          operatorGroup: operatorGroupPayload,
        },
        callback: () => {
          markNotificationAsRead(notification);
          toastMessages.generateToastr(
            'success',
            'Email sent',
            'Customer claim email was approved and sent.'
          );
          setOpen(false);
        },
        errorCallback: (error: any) => {
          if (!error?.statusCode && !error?.responseStatus) {
            toastMessages.apiError(error);
          }
        },
      })
    );
  };

  const renderWhatsNewList = () => {
    const filteredNotifications = filteringRead
      ? whatsNewNotifications.filter((notification: any) => {
          const hasUnreadTracking = Array.isArray(notification?.trackingRecords)
            ? notification.trackingRecords.some((record: any) => record && record.read === false)
            : false;
          return hasUnreadTracking;
        })
      : whatsNewNotifications;

    const sortedNotifications = [...filteredNotifications]
      .map((notification) => {
        const hasUnreadTracking = Array.isArray(notification?.trackingRecords)
          ? notification.trackingRecords.some((record: any) => record && record.read === false)
          : false;
        const parsedBody = parseNotificationBody(notification?.body);
        const rawMessage = typeof notification?.body === 'string' ? notification.body : '';
        const fallbackMessage = parsedBody.message || rawMessage;
        const sanitizedHtml = sanitizeHtmlContent(fallbackMessage);
        const plainTextMessage = stripHtmlTags(sanitizedHtml);
        const linkUrl = normalizeExternalLink(notification?.link || parsedBody.linkUrl);
        const mediaItems = Array.isArray(notification?.media) ? notification.media : [];
        const imageMedia = mediaItems.filter(
          (entry: any) => typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('image')
        );
        const videoMedia = mediaItems.filter(
          (entry: any) => typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('video')
        );
        const imageSrc = imageMedia[0]?.downloadUrl || parsedBody.imageData || parsedBody.imageUrl;
        const videoSrc = videoMedia[0]?.downloadUrl || parsedBody.videoData || parsedBody.videoUrl;

        return {
          ...notification,
          hasUnreadTracking,
          message: plainTextMessage,
          messageHtml: sanitizedHtml,
          linkUrl,
          mediaItems,
          imageSrc,
          videoSrc,
          truncatedTitle:
            notification?.title && notification.title.length > 30
              ? `${notification.title.slice(0, 27)}...`
              : notification?.title,
          truncatedDescription:
            plainTextMessage && plainTextMessage.length > 80
              ? `${plainTextMessage.slice(0, 77)}...`
              : plainTextMessage,
        };
      })
      .sort((a, b) => {
        const newest = a?.createdAt ? moment(a.createdAt).valueOf() : 0;
        const oldest = b?.createdAt ? moment(b.createdAt).valueOf() : 0;

        if (a.hasUnreadTracking && !b.hasUnreadTracking) return -1;
        if (!a.hasUnreadTracking && b.hasUnreadTracking) return 1;

        if (newest === oldest) return 0;
        return newest > oldest ? -1 : 1;
      });

    return (
      <div className="alerts-list whats-new-list">
        {sortedNotifications.map((n: any) => (
          <div
            className={`alert-item whats-new-card${n.hasUnreadTracking ? ' unread' : ''}`}
            role="menuitem"
            key={n.id}
            onClick={() => handleNotificationOpen(n)}
          >
            {n.hasUnreadTracking && <span className="unread-dot" />}
            <div className="alert-description">
              <div
                className="whats-new-header"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  // marginBottom: '0.5rem',
                }}
              >
                <p
                  className="font-weight-bold"
                  style={{
                    marginBottom: 0,
                    marginRight: '0.5rem',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                  title={n.title}
                >
                  {n.truncatedTitle || n.title}
                </p>
              </div>
              <span style={{ fontSize: 'smaller', fontWeight: 100, color: '#888' }}>
                {n.createdAt ? moment(n.createdAt).fromNow() : ''}
              </span>
            </div>
            {canManageWhatsNew && (
              <button
                type="button"
                className="whats-new-edit-button"
                onClick={(event) => handleEditNotification(n, event)}
                style={{
                  border: '1px solid #ccc',
                  background: 'transparent',
                  padding: '1px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <MdOutlineEdit style={{ fontSize: '1.2rem' }} />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderClaimNotificationCards = (notifications: any[]) => {
    const filteredNotifications = filteringRead
      ? notifications.filter((notification: any) => {
          const hasUnreadTracking = Array.isArray(notification?.trackingRecords)
            ? notification.trackingRecords.some((record: any) => record && record.read === false)
            : false;
          return hasUnreadTracking;
        })
      : notifications;

    const sortedNotifications = [...filteredNotifications]
      .map((notification) => {
        const hasUnreadTracking = Array.isArray(notification?.trackingRecords)
          ? notification.trackingRecords.some((record: any) => record && record.read === false)
          : false;
        const parsedBody = parseNotificationBody(notification?.body);
        const rawMessage = typeof notification?.body === 'string' ? notification.body : '';
        const fallbackMessage = parsedBody.message || rawMessage;
        const sanitizedHtml = sanitizeHtmlContent(fallbackMessage);
        const plainTextMessage = stripHtmlTags(sanitizedHtml);
        const displayTitle =
          notification?.type === STALLED_CLAIM_NOTIFICATION_TYPE
            ? 'Claim requires approval/denial'
            : notification?.type === CLAIM_CREATED_NOTIFICATION_TYPE
            ? 'New claim created'
            : notification?.title;
        const isClaimEmailApproval =
          getNotificationType(notification) === CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE;

        return {
          ...notification,
          hasUnreadTracking,
          message: plainTextMessage,
          displayTitle,
          isClaimEmailApproval,
          truncatedTitle:
            displayTitle && displayTitle.length > 30
              ? `${displayTitle.slice(0, 27)}...`
              : displayTitle,
          truncatedDescription:
            plainTextMessage && plainTextMessage.length > 80
              ? `${plainTextMessage.slice(0, 77)}...`
              : plainTextMessage,
        };
      })
      .sort((a, b) => {
        const newest = a?.createdAt ? moment(a.createdAt).valueOf() : 0;
        const oldest = b?.createdAt ? moment(b.createdAt).valueOf() : 0;

        if (a.hasUnreadTracking && !b.hasUnreadTracking) return -1;
        if (!a.hasUnreadTracking && b.hasUnreadTracking) return 1;

        if (newest === oldest) return 0;
        return newest > oldest ? -1 : 1;
      });

    return (
      <div className="alerts-list whats-new-list">
        {sortedNotifications.map((n: any) => (
          <div
            className={`alert-item whats-new-card${n.hasUnreadTracking ? ' unread' : ''}`}
            role="menuitem"
            key={n.id}
            onClick={() => handleNotificationOpen(n)}
          >
            {n.hasUnreadTracking && <span className="unread-dot" />}
            <div className="alert-description">
              <div
                className="whats-new-header"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <p
                  className="font-weight-bold"
                  style={{
                    marginBottom: 0,
                    marginRight: '0.5rem',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                  title={n.displayTitle || n.title}
                >
                  {n.truncatedTitle || n.displayTitle || n.title}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {n.isClaimEmailApproval && (
                    <button
                      type="button"
                      className="whats-new-link-button"
                      onClick={(event) => approveAndSendClaimEmail(n, event)}
                    >
                      Approve & Send Email
                    </button>
                  )}
                  {coreSiteId && getClaimIdFromNotification(n) && (
                    <button
                      type="button"
                      className="whats-new-link-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        markNotificationAsRead(n);
                        setOpen(false);
                        history.push(`/${coreSiteId}/claims`, {
                          openDrawerId: Number(getClaimIdFromNotification(n)),
                        });
                      }}
                    >
                      View Claim
                    </button>
                  )}
                </div>
              </div>
              <span style={{ fontSize: 'smaller', fontWeight: 100, color: '#888' }}>
                {n.createdAt ? moment(n.createdAt).fromNow() : ''}
              </span>
              {n.truncatedDescription && (
                <p style={{ margin: '0.25rem 0 0', fontSize: 12, color: '#555' }}>
                  {n.truncatedDescription}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderClaimsList = () => renderClaimNotificationCards(claimNotifications);

  const renderEmptyState = () => (
    <p className="alerts-message" role="menuitem">
      {intl.formatMessage({ id: 'noAlerts' })}
    </p>
  );

  const renderSelectedContent = () => {
    if (isWhatsNewView) {
      return whatsNewNotifications.length ? renderWhatsNewList() : renderEmptyState();
    }

    if (isClaimsView) {
      return claimNotifications.length ? renderClaimsList() : renderEmptyState();
    }

    if (filteredAlerts.length) {
      return handleNotificationList();
    }

    return renderEmptyState();
  };

  const handleAddUpdateClick = () => {
    setOpen(false);
    dispatch(
      showDialog('WHATS_NEW_UPDATE', {
        defaultModal: false,
      })
    );
  };

  const handleEditNotification = (notification: any, event?: MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.stopPropagation();
    }
    if (!notification) return;
    setOpen(false);
    dispatch(
      showDialog('WHATS_NEW_UPDATE', {
        defaultModal: false,
        notification,
      })
    );
  };

  const showAddUpdateButton = canManageWhatsNew && selectedType === whatsNewTypeValue;
  const totalUnreadNotifications = unreadCount + readTrackingCount + claimUnreadCount;

  return (
    <div className="alert-container">
      {totalUnreadNotifications > 0 ? (
        <span className="alert-number" data-testid="alerts-number">
          {totalUnreadNotifications}
        </span>
      ) : (
        ''
      )}
      <Dropdown className="n-main-dropdown" isOpen={open} toggle={toggleOpen}>
        <DropdownToggle
          className={`btn btn-secondary alert-button${unreadCount > 0 ? ' has-alerts' : ''}`}
        >
          <i className="icon icon-bell"></i>
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-right" right>
          <div className="alerts-content" data-testid="alerts-dropdown-content">
            <div className="alerts-title">
              <p>{intl.formatMessage({ id: 'notifications' })}</p>
              <ToggleButton
                label={intl.formatMessage({
                  id: 'onlyShowUnread',
                  defaultMessage: 'Only show unread',
                })}
                initialState={filteringRead}
                toggleCallback={handleFilterToggle}
                leftLabel={true}
              />
            </div>
            <ul className="alert-categories">
              <li>
                {alertTypeButton(intl.formatMessage({ id: 'all', defaultMessage: 'All' }), null)}
              </li>
              {Object.entries(alertTypes).map((type: any[], i: any) => {
                const typeLabel = type[0];
                const typeValue = type[1];
                const isWhatsNewType = typeValue === whatsNewTypeValue;
                const isClaimsType = typeValue === claimsTypeValue;
                const showWhatsNewBadge = isWhatsNewType && readTrackingCount > 0;
                const showClaimsBadge = isClaimsType && claimUnreadCount > 0;

                return (
                  <li key={i} className="alert-category-item">
                    <div className="alert-category-button-wrapper">
                      {alertTypeButton(typeLabel, typeValue)}
                      {showWhatsNewBadge && (
                        <span className="notification-badge notification-badge-red whats-new-badge">
                          {readTrackingCount}
                        </span>
                      )}
                      {showClaimsBadge && (
                        <span className="notification-badge notification-badge-red whats-new-badge">
                          {claimUnreadCount}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {showAddUpdateButton && (
              <div
                className="alerts-admin-actions"
                style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  style={{ padding: '2px 8px', fontSize: 12, fontWeight: 600, color: '#000' }}
                  onClick={handleAddUpdateClick}
                >
                  {intl.formatMessage({ id: 'shareUpdate', defaultMessage: '+ New update' })}
                </button>
              </div>
            )}
            {renderSelectedContent()}
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default Alerts;
